import { Component, createElement } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { user } from 'helper/currentUser';
import { NarrationStateEnum } from 'appConsts/narrationStateEnum';
import contextTypes from './context-types';
import getVendor from './utils/get-vendor';
import requestFullscreen from './utils/request-fullscreen';
import exitFullscreen from './utils/exit-fullscreen';
import fullscreenChange from './utils/fullscreen-change';

const MEDIA_EVENTS = {
  onPlay: 'isPlaying',
  onPause: 'isPlaying',
  onError: null,
  onDuration: 'duration',
  onProgress: 'progress',
  onTimeUpdate: 'currentTime',
  onMute: 'isMuted',
  onVolumeChange: 'volume'
};
let narrationState = {};
// const user = new CurrentUser();
const MEDIA_EVENTS_KEYS = Object.keys(MEDIA_EVENTS);
class Media extends Component {
  static propTypes = {
    vendor: PropTypes.oneOf(['youtube', 'vimeo', 'audio', 'video']).isRequired,
    src: PropTypes.string.isRequired,
    children: PropTypes.func.isRequired,
    autoPlay: PropTypes.bool,
    loop: PropTypes.bool
  };

  static defaultProps = {
    autoPlay: false,
    loop: false
  };

  static childContextTypes = contextTypes;
  state = {
    currentTime: 0,
    progress: 0,
    duration: 0.1,
    volume: 1,
    isLoading: true,
    isPlaying: false,
    isMuted: false,
    isFullscreen: false
  };

  _lastVolume = 0;
  getChildContext() {
    return {
      ...this.state,
      play: this._handlePlay,
      pause: this._handlePause,
      playPause: this._handlePlayPause,
      stop: this._handleStop,
      seekTo: this._handleSeekTo,
      mute: this._handleMute,
      muteUnmute: this._handleMuteUnmute,
      setVolume: this._handleSetVolume,
      fullscreen: this._handleFullscreen
    };
  }

  componentDidMount() {
    fullscreenChange('add', this._handleFullscreenChange);
  }

  componentDidUpdate() {
    if (this.state.currentTime > 0) {
      narrationState[this.props.src] = this.state.currentTime;
      localStorage.setItem(user.id() + '_mediaDuration', JSON.stringify(narrationState));
    }
  }

  UNSAFE_componentWillUpdate(nextProps) {
    // clean state if the video has changed
    if (this.props.src !== nextProps.src) {
      // eslint-disable-next-line react/no-will-update-set-state
      this.setState({
        currentTime: 0,
        progress: 0,
        duration: 0,
        isPlaying: false
      });
    }
  }

  componentWillUnmount() {
    fullscreenChange('remove', this._handleFullscreenChange);
  }

  get _mediaEvents() {
    const events = {};
    MEDIA_EVENTS_KEYS.forEach(key => {
      const stateKey = MEDIA_EVENTS[key];
      const propCallback = this.props[key];
      events[key] = val => {
        if (stateKey) {
          this.setState({ [stateKey]: val });
        }
        if (typeof propCallback === 'function') {
          propCallback(val);
        }
      };
    });
    return events;
  }

  getAndSetPreviousTimeOfAudio = () => {
    const userId = user.id();
    const mediaDurations = JSON.parse(localStorage.getItem(`${userId}_mediaDuration`));
    if (mediaDurations) {
      narrationState = {
        ...mediaDurations
      };
      const previousEndTime = narrationState[this.props.src];
      if (previousEndTime && previousEndTime > 5 && this.state.duration !== previousEndTime) {
        this.state.currentTime = previousEndTime - NarrationStateEnum.mediaBufferState; // in audio reverse calculation for time
      }
    }
  };

  _handleOnReady = () => {
    const { volume, isMuted } = this.state;
    this._handleSetVolume(volume);
    this._handleMute(isMuted);
    if (this.state.currentTime === 0) {
      this.getAndSetPreviousTimeOfAudio();
    }
    if (this.props.autoPlay) {
      this._handlePlay();
    }
    this.setState({ isLoading: false });
  };

  _handleOnEnded = () => {
    const { loop, onEnded } = this.props;
    if (loop) {
      this._handleSeekTo(0);
      this._handlePlay();
    } else {
      this.setState({ isPlaying: false });
    }
    if (typeof onEnded === 'function') {
      onEnded();
    }
  };

  initialSeekableFlag = true;
  _handlePlay = () => {
    if (this.initialSeekableFlag) {
      this._handleSeekTo(this.state.currentTime); // need to call for start video at specific time
      this.initialSeekableFlag = false;
    }
    const playPromise = this._player.play();
    if (playPromise !== undefined) {
      playPromise.catch(e => {
        console.log('Error in play');
        window.analytics.track('PLAY_FAILED', {
          errorMessage: e.message,
          errorType: e.type,
          errorStack: e.stack
        });
      });
    }
  };

  _handlePause = () => {
    this._player.pause();
  };

  _handlePlayPause = () => {
    if (!this.state.isPlaying) {
      this._handlePlay();
    } else {
      this._player.pause();
    }
  };

  _handleStop = () => {
    this._player.stop();
  };

  _handleSeekTo = currentTime => {
    this._player.seekTo(currentTime);
    this.setState({ currentTime });
  };

  _handleMute = isMuted => {
    if (isMuted) {
      this._lastVolume = this.state.volume;
      this._player.setVolume(0);
    } else {
      const volume = this._lastVolume > 0 ? this._lastVolume : 0.1;
      this._player.setVolume(volume);
    }
    this._player.mute(isMuted);
  };

  _handleMuteUnmute = () => {
    this._handleMute(!this.state.isMuted);
  };

  _handleSetVolume = volume => {
    let isMuted = false;
    if (volume <= 0) {
      isMuted = true;
    }
    if (isMuted !== this.state.isMuted) {
      this._handleMute(isMuted);
    } else {
      this._lastVolume = volume;
    }
    this._player.setVolume(volume);
  };

  _handleFullscreen = () => {
    if (!this.state.isFullscreen) {
      // eslint-disable-next-line react/no-find-dom-node
      ReactDOM.findDOMNode(this._player)[requestFullscreen]();
    } else {
      document[exitFullscreen]();
    }
  };

  _handleFullscreenChange = ({ target }) => {
    // eslint-disable-next-line react/no-find-dom-node
    if (target === ReactDOM.findDOMNode(this._player)) {
      this.setState({ isFullscreen: !this.state.isFullscreen });
    }
  };

  render() {
    const { src, children, autoPlay } = this.props;
    const { vendor, component } = getVendor(src, this.props.vendor);
    return (
      component &&
      children(
        createElement(component, {
          ref: c => (this._player = c),
          vendor,
          src,
          autoPlay,
          onReady: this._handleOnReady,
          onEnded: this._handleOnEnded,
          ...this._mediaEvents
        })
      )
    );
  }
}
export default Media;
